/* eslint-disable react-hooks/exhaustive-deps */
import { Col, Row, Select, Input, message, Modal, Form, Button, Spin, Switch } from "antd";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import ReactJoyride from "react-joyride";
import { DOMAIN_ID, ORG_ID, REACT_APP_API_TENANTS_URL, REACT_APP_API_DID_URL, AUTH_TOKEN } from "../../constants";
import { LoadingOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { isEmpty } from "../../utils/helpers";
import { FormInput, UserInput, UserSelect } from "../ui/UserInteraction";
import { awsServerRegion } from "../../utils/awsServer";
import Typography from "antd/es/typography/Typography";
import { getMFAProvider,setMFAProvider } from "../../service/tenantService";
import DataInsertModal from "../ui/modal/DataInsertModal";
import { getDefaultIssuerforTenant, getIssuerList } from "../../service/tenantService";
import AuthFactors from "./tenantConfig/authFactors";
import ConditionalAccess from "./tenantConfig/conditionalAccess";
import { SsoConfig } from "./tenantConfig/ssoConfig/ssoMethods";
import TenantLogo from "./tenantConfig/tenantLogo";
import MfaFactorsPage from "../settings/mfaFactors/MfaFactors";
import {
  AlertTriangle,
  AppWindow,
  Archive,
  BadgeCheck,
  Building2,
  ChevronRight,
  Clock,
  FileText,
  Image as ImageIcon,
  KeyRound,
  Lock,
  LogIn,
  Network,
  Plug,
  ShieldAlert,
  Tags,
  User,
  Video,
} from "lucide-react";
import { AN_COLORS, AN_FONT } from "../../design-system/tokens";
import {
  SettingsCard,
  SettingsPage,
  SettingsRow,
  SettingsSection,
  SETTINGS_TOKENS,
} from "../settings/settingsUi";

const { Text } = Typography;

const TIME_ZONES = moment.tz.names().map((tz) => {
  const offset = moment.tz(tz).format("Z"); // Get the offset in the format "+05:30"
  return {
    label: `${tz} (GMT${offset})`, // Include offset in the label
    value: tz,
  };
});

// Scoped restyle of the legacy panel bodies so their labels and selects read
// like SettingsField rows (label above a full-width control).
const TENANT_CONFIG_CSS = `
  .an-tenant-config .search-label {
    margin: 0 0 8px;
    font-size: 13.5px;
    font-weight: 600;
    color: ${AN_COLORS.ink};
    font-family: ${AN_FONT};
  }
  .an-tenant-config .an-config-row-head:hover { background: ${AN_COLORS.bg}; }
  .an-tenant-config .an-config-row-head:focus-visible {
    outline: 2px solid ${AN_COLORS.primary};
    outline-offset: -2px;
  }
  .an-tenant-config .an-config-row-body .ant-select { width: 100%; }
`;

// Local primitive (not in settingsUi): a SettingsRow-shaped header that
// expands its body inside the same card — replaces the old antd Collapse
// panels. Like Collapse's defaults, a body mounts on first open and then stays
// mounted (hidden while collapsed), so inner form state survives a collapse.
const ConfigRow = ({ id, icon, title, description, open, mounted, onToggle, last = false, children }) => (
  <div
    id={`tenant-config-${id}`}
    style={{ borderBottom: last ? "none" : `1px solid ${SETTINGS_TOKENS.hairline}` }}
  >
    <button
      type="button"
      className="an-config-row-head"
      onClick={onToggle}
      aria-expanded={open}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        width: "100%",
        padding: SETTINGS_TOKENS.rowPad,
        textAlign: "left",
        background: open ? AN_COLORS.bg : "transparent",
        border: "none",
        cursor: "pointer",
        fontFamily: AN_FONT,
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: AN_COLORS.primaryL,
          color: AN_COLORS.primary,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14.5, fontWeight: 600, color: AN_COLORS.ink }}>{title}</span>
        {description && (
          <span style={{ display: "block", fontSize: 12.5, color: AN_COLORS.muted, marginTop: 2, lineHeight: 1.45 }}>
            {description}
          </span>
        )}
      </span>
      <ChevronRight
        size={16}
        color={open ? AN_COLORS.primary : AN_COLORS.muted}
        style={{ flexShrink: 0, transition: "transform 160ms ease", transform: open ? "rotate(90deg)" : "none" }}
      />
    </button>
    {mounted && (
      <div
        className="an-config-row-body"
        style={{
          display: open ? "block" : "none",
          padding: "16px 20px 20px 72px",
          borderTop: `1px solid ${SETTINGS_TOKENS.hairline}`,
        }}
      >
        {children}
      </div>
    )}
  </div>
);

const CustomVaultModal = (isModalOpen, handleCancel, handleOk) => {
  return (
    <Modal
      title="Enter the Custom Vault Configurations"
      open={isModalOpen}
      onCancel={handleCancel}
      centered={true}
      // onOk={handleOk}
      footer={null}
    >
      <Form
        layout="vertical"
        onFinish={handleOk}
        style={{ paddingTop: "15px" }}
        initialValues={{
          port: 8200,
        }}
      >
        <Form.Item
          label="Root Token"
          name="rootToken"
          rules={[
            {
              required: true,
              message: "Enter root token",
            },
          ]}
        >
          <Input placeholder="Enter root token" />
        </Form.Item>

        <Form.Item
          label="IP Address"
          name="ipAddress"
          rules={[
            {
              required: true,
              message: "Enter IP address",
            },
          ]}
        >
          <Input placeholder="Enter IP address" />
        </Form.Item>
        <Form.Item label="Port" name="port">
          <Input disabled />
        </Form.Item>
        <Form.Item style={{ textAlign: "end" }}>
          <Button type="primary" htmlType="submit">
            Add
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const TenantConfig = () => {
  const [tenantData, setTenantData] = useState([]);
  const [notification, setNotification] = message.useMessage();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mfa = queryParams.get("mfa");
  const type = queryParams.get("type");
  // Which accordion panel to open on arrival. Set by the /pam/settings/mfa
  // redirect so an old bookmark still lands on the factors list rather than on
  // a collapsed accordion the user has to go hunting through.
  const panel = queryParams.get("panel");
  // This page is also mounted at /onboard/setup/tenantConfig, where a
  // personal-account panel does not belong — that flow configures the tenant.
  const isOnboardRoute = location.pathname.startsWith("/onboard");

  // Expanded rows (was the Collapse's defaultActiveKey): the ?panel= deep link,
  // plus the onboarding hand-offs (?mfa= and ?type=pamLocal).
  const [activeKey, setActiveKey] = useState(() => [
    ...(panel ? [panel] : []),
    ...(mfa ? ["0", "1", "4"] : type === "pamLocal" ? ["1"] : []),
  ]);
  // Rows opened at least once; their bodies stay mounted (see ConfigRow).
  const [mountedKeys, setMountedKeys] = useState(activeKey);
  const toggleRow = (key) => {
    setActiveKey((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
    setMountedKeys((keys) => (keys.includes(key) ? keys : [...keys, key]));
  };
  const rowProps = (key) => ({
    id: key,
    open: activeKey.includes(key),
    mounted: mountedKeys.includes(key),
    onToggle: () => toggleRow(key),
  });

  // A ?panel= link that arrives while the page is already mounted opens that
  // row too; once the rows render, bring the linked one into view.
  useEffect(() => {
    if (!panel) return;
    setActiveKey((keys) => (keys.includes(panel) ? keys : [...keys, panel]));
    setMountedKeys((keys) => (keys.includes(panel) ? keys : [...keys, panel]));
  }, [panel]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [vaultDetails, setVaultDetails] = useState({
    rootToken: "",
    ipAddress: "",
    port: 8200,
    vaultType: "",
  });
  const [defaultIssuerId, setDefaultIssuerId] = useState(null);
  const [ issuerDetails, setIssuerDetails ]= useState([]);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (!panel || isLoading) return;
    document.getElementById(`tenant-config-${panel}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [panel, isLoading]);
  const [tenantLogo, setTenantLogo] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const [ditStatus, setDitStatus] = useState(null);
  const [tenantTimezone, setTenantTimezone] = useState("");

  const { Option } = Select;

  const [isOpenAuthProvider, setOpenAuthProvider] = useState(false);
  const [newMFAForm] = Form.useForm();
  const [breakglassDisable, setBreakglassDisable] = useState(false);

  const [sessionRecording, setSessionRecorinding] = useState({
    sessionRecordingKey: "",
    sessionRecordingSecret: "",
    sessionRecordingRegion: "",
    sessionRecordingBucketName: "",
    isEdit: false,
    isSave: false,
    isUserEditable: false
  })
  const [sessionRecordingSecret, setSessionRecordingSecret] = useState("");

  //method
  const handleSessionSetState = (updatedObj) => {
    setSessionRecorinding((prev) => ({ ...prev, ...updatedObj }));
  }

  const handleOk = async (e) => {
    let payload = {
      orgId: ORG_ID(),
      tenantId: `${DOMAIN_ID()}`,
      vaulttype: "3",
      port: `${e.port}`,
      ipAddress: e.ipAddress,
      rootToken: e.rootToken,
    };

    try {
      const res = await axios.post(
        `${REACT_APP_API_DID_URL}/secretservice/createTenantVaultConfig`,
        payload,
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      );
      notification.open({
        type: "success",
        content: "Vault configuration added successfully",
      });

      if (res.data.code === 200) {
        setVaultDetails((prev) => ({
          ...prev,
          rootToken: e.rootToken,
          ipAddress: e.ipAddress,
          port: e.port,
        }));
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error("handleOk failed:", error?.response?.status, error?.message);
      notification.open({
        type: "error",
        content: "Vault configuration failed",
      });
    }
  };

  useEffect(() => {
    fecthMFAProviders();
    fetchTenantDetails();
    fetchIssuerList();
  }, []);

  const [isOnBoarding, setIsOnboarding] = useState(false);
  const [run, setRun] = useState(true);

  useEffect(() => {
    //TODO : Onboarding api call done here
    setIsOnboarding(mfa ? true : false);
  }, []);

  const fecthMFAProviders = () => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
    }
    getMFAProvider({ payload, onSuccess: getMFAProviderSuccess, onError: getMFAProviderError })
  }

  const getMFAProviderSuccess = (response) => {
    const data = response?.data;
    setOpenAuthProvider(false);
    
  }

  const getMFAProviderError = (response) => {
    console.error("getMFAProviderError:", response?.response?.status, response?.message)
  }

  const addMFAProviders = (values) => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      ...values
    }
    setMFAProvider({ payload, onSuccess: addMFAProviderSuccess, onError: addMFAProviderError })
  }

  const addMFAProviderSuccess = (response) => {
    newMFAForm.resetFields();
    notification.success(response?.message);
    fecthMFAProviders();
  }

  const addMFAProviderError = (response) => {
    console.error("addMFAProviderError:", response?.response?.status, response?.message)
  }

  // 
  const handleAddMFAProvider = async () => {
    const values = await newMFAForm.getFieldsValue();
    if (!isEmpty(values?.name) && !isEmpty(values?.description)) {
      try {
        addMFAProviders(values);
      } catch (error) {
        message.error('Error :', error.message);
      }
    } else {
      message.error('Please fill in all required fields.');
    }
  }

  const fetchIssuerList = async () => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
    }
    getIssuerList({ payload, onSuccess: getIssuerListSuccess, onError: getIssuerListError })
  }

  const getIssuerListSuccess = (response) => {
    const { issuers } = response;
    const defaultIssuer = issuers?.find((item) => item?.is_default)
    const issuersUpdated= issuers?.map((item) => ({ label: item?.issuer_name, value: item?.id }))
    setIssuerDetails(issuersUpdated);
    setDefaultIssuerId(defaultIssuer?.id)
  }

  const getIssuerListError = (response) => {
    console.error("getIssuerListError:", response?.response?.status, response?.message)
  }

  const startTour = () => {
    const tourStep = [
      {
        // target: "#onboarding_step",
        content: (
          <span>
            <h4 style={{ marginBottom: "0" }}>What is a tenant config screen?</h4>
            <p>Tenant config screen allows you to:</p>
            <ol>
              <li>configure tenant mfa</li>
              <li>configure authentication mfa</li>
              <li>Decide how policies are enforced and more</li>
            </ol>
          </span>
        ),
        disableBeacon: true,
        placement: "center",
        target: "body",
        styles: {
          options: {
            width: 500,
          },
        },
        // title: "MFA Device",
      },
      {
        target: "#onboarding_step1",
        content: (
          <span>
            <h4 style={{ marginBottom: "0" }}>Select any MFA Device </h4>
            <br />
            Multi-Factor Authentication (MFA) adds an extra layer of security by requiring users to
            provide two or more verification factors before gaining access.
          </span>
        ),
        disableBeacon: true,

        // title: "MFA Device",
      },
      {
        target: "#onboarding_step2",
        content: (
          <span>
            {" "}
            <h4 style={{ marginBottom: "0" }}>Select any Endpoint Authentication MFA method </h4>
            <br />
            Selecting an Endpoint Authentication Multi-Factor Authentication (MFA) method involves
            choosing a specific approach to verify the identity of users during the authentication
            process. This step is crucial for enhancing security.{" "}
          </span>
        ),
        disableBeacon: true,
        placement: "right",

        // title: "Endpoint Authentication MFA",
      },
      {
        target: "#onboarding_step3",
        content: (
          <span>
            <h4 style={{ marginBottom: "0" }}> Select any Authentication Policy</h4>
            <br />
            Selecting an Authentication Policy involves defining rules and guidelines that dictate
            how user authentication is managed within a system. This policy helps shape the security
            framework and user access controls.{" "}
          </span>
        ),
        disableBeacon: true,
        // title: "Authentication Policy",
      },
    ];
    const tourPamLocal = [
      {
        target: "#onboarding_step4",
        content: (
          <span>
            <h4 style={{ marginBottom: "0" }}> Select any SSO MFA </h4>
            <br />
            Viewing audited policies involves examining a record of the authentication policies that
            have been audited within a system. This process provides insights into the historical
            performance, effectiveness, and outcomes of implemented security measures.
          </span>
        ),
        disableBeacon: true,
        // title: "MFA Device",
      },
    ];
    const handleTourCallback = ({ status }) => {
      if (status === "finished" || status === "skipped") {
        setRun(false);
      }
    };

    return (
      <ReactJoyride
        className="tour-container"
        steps={mfa ? tourStep : type === "pamLocal" ? tourPamLocal : tourStep}
        run={run}
        floaterProps={{ disableAnimation: true }}
        callback={handleTourCallback}
        continuous
        scrollToFirstStep
        disableScrollParentFix
        scrollOffset={100}
        showSkipButton
        showProgress
        hideCloseButton={true}
        styles={{
          buttonNext: {
            backgroundColor: "blue",
            color: "#ffffff",
          },
          buttonBack: {
            color: "#1677ff",
          },
        }}
      />
    );
  };

  /// methods
  const handleMaskedInput = (e) => {
    const inputType = e.nativeEvent?.inputType;
    const newChar = e.nativeEvent?.data;
    const inputValue = e.target.value;

    if (inputType === 'deleteContentBackward') {
      if (inputValue === '') {
        // Full delete (select-all + backspace)
        handleSessionSetState({ sessionRecordingSecret: "" })
        setSessionRecordingSecret('*'.repeat(0));
      } else {
        // Handle backspace
        const updated = sessionRecording?.sessionRecordingSecret.slice(0, -1);
        handleSessionSetState({ sessionRecordingSecret: updated })
        setSessionRecordingSecret('*'.repeat(updated.length));
      }

    } else if (newChar) {
      // Handle regular typing
      const updated = sessionRecording?.sessionRecordingSecret + newChar;
      handleSessionSetState({ sessionRecordingSecret: updated })
      setSessionRecordingSecret('*'.repeat(updated.length));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const updated = pastedText;
    handleSessionSetState({ sessionRecordingSecret: updated })
    setSessionRecordingSecret('*'.repeat(updated.length));
  };

  const fetchTenantDetails = () => {
    let tenantPayload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
    };
    setIsLoading(true);
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/getTenantDetail`, tenantPayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        setIsLoading(false);
        if (res && res.data && res.data.data) {
          const data = res.data.data;
          setTenantData(res.data?.data);
          setDitStatus(res?.data?.data?.is_dit_enabled === "0" ? "Disabled" : "Enabled");
          setTenantTimezone(res?.data?.data?.timezone);
          if (res?.data?.data?.imageUrl) {
            setTenantLogo(res?.data?.data?.imageUrl);
          }
          setVaultDetails((prev) => ({
            ...prev,
            vaultType:
              res?.data?.data?.vaultFlag === "1"
                ? "Default"
                : res?.data?.data?.vaultFlag === "2"
                  ? "Centralized Vault"
                  : "Custom Vault",
          }));
          // if ( !isEmpty(data?.sessionrecordingbucketname) && !isEmpty(data?.sessionrecordingkey) && !isEmpty(data?.seesionrecordingsecret) && !isEmpty(data?.sessionrecordingregion)) {
          setSessionRecordingSecret('*'.repeat(data?.seesionrecordingsecret?.length));
          setBreakglassDisable(data?.disablerootaccess);
          handleSessionSetState({
            sessionRecordingKey: data?.sessionrecordingkey,
            sessionRecordingSecret: data?.seesionrecordingsecret,
            sessionRecordingRegion: data?.sessionrecordingregion,
            sessionRecordingBucketName: data?.sessionrecordingbucketname,
            isEdit: true,
            isSave: true,
            isUserEditable: false
          })
          // }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error("fetchTenantDetails failed:", err?.response?.status, err?.message);
        notification.open({
          type: "error",
          content: `Unable to fetch tenant details`,
        });
      });
  };

  const handleBreakglass = async (isChecked) => {
    setBreakglassDisable(isChecked);
    let payload = {
      tenantId: `${DOMAIN_ID()}`,
      orgId: `${ORG_ID()}`,
      disable_root_access: isChecked
    };

    try {
      const response = await axios.post(
        `${REACT_APP_API_TENANTS_URL}/tenants/disable_root`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      message.success(response?.data?.message);
    } catch (error) {
      message.error("Unable to update the break glass root access.");
    }
  }


  const handleConfigureDefaultIssuer = () => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      issuerId: defaultIssuerId
    }
    getDefaultIssuerforTenant({ payload, onSuccess: getDefaultIssuerforTenantSuccess, onError: getDefaultIssuerforTenantError })
  }

  const getDefaultIssuerforTenantSuccess = (response) => {
    const { message } = response;
    notification.success(message);
  }

  const getDefaultIssuerforTenantError = (error) => {
    console.error("getDefaultIssuerforTenantError ==>", error?.response?.status ?? error?.status, error?.message)
  }

  const updateVaultType = (e, val) => {
    let vaultTypePayload = {
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      vault_type: e,
    };

    e === "Custom Vault" ? setIsModalOpen(true) : setIsModalOpen(false);

    setVaultDetails((prevState) => ({
      ...prevState,
      vaultType: e,
    }));

    axios
      .post(`${REACT_APP_API_DID_URL}/secretservice/createVaultFlag`, vaultTypePayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `Vault type updated successful to ${val.children}`,
        });
      })
      .catch((err) => {
        console.error("updateVaultType failed:", err?.response?.status, err?.message);
        notification.open({
          type: "error",
          content: `Vault type update failed.`,
        });
      });
  };

  const updateMfaDevices = (e, val) => {
    console.log({ e, val });
    let mfaDevicePayload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      mfadevices: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setMfaDevices`, mfaDevicePayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `MFA Device updated successful to ${val.children}`,
        });
      })
      .catch((err) => {
        console.error("updateMfaDevices failed:", err?.response?.status, err?.message);
      });
  };
  const updateSsoMfa = (e, val) => {
    let SsoMfaPayload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      ssomfa: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setSsoMfa`, SsoMfaPayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `Admin SSO MFA ${val.children} successful`,
        });
      })
      .catch((err) => {
        console.error("updateSsoMfa failed:", err?.response?.status, err?.message);
        notification.open({
          type: "error",
          content: `Unable to update Admin SSO MFA`,
        });
      });
  };

  const updateAdminMFACache = (e, val) => {
    let adminMFACache = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      adminmfacache: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setAdminMfaCache`, adminMFACache, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `Push notification ${e === 0 ? "will" : "won't"} show on your device`,
        });
      })
      .catch((err) => {
        console.error("updateAdminMFACache failed:", err?.response?.status, err?.message);
        notification.open({
          type: "error",
          content: `Unable to update push notification for endpoint authentication`,
        });
      });
  };

  const updateAuthenticationPolicy = (e, val) => {
    let AuthenticationPolicyPayload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      authenticationpolicy: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setAuthenticationPolicy`, AuthenticationPolicyPayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `Authentication Policy updated successful to ${val.children}`,
        });
      })
      .catch((err) => {
        console.error("updateAuthenticationPolicy failed:", err?.response?.status, err?.message);
      });
  };

  const updateConnectionMode = (e, val) => {
    let ConnectionModePayload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      connectionmode: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setConnectionMode`, ConnectionModePayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `Connection Mode updated successful to ${val.children}`,
        });
      })
      .catch((err) => {
        console.error("updateConnectionMode failed:", err?.response?.status, err?.message);
      });
  };

  const updateAuthenticationMechanism = (e, val) => {
    let authMechPayload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      credentialstore: e,
    };

    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setCredentialMode`, authMechPayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `Credential Mode updated successful to ${val.children}`,
        });
      })
      .catch((err) => {
        console.error("updateAuthenticationMechanism failed:", err?.response?.status, err?.message);
      });
  };

  const updateCredentialStore = (e, val) => {
    let CredentialStorePayload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      credentialstore: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setCredentialStore`, CredentialStorePayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `Credential Store updated successful to ${val.children}`,
        });
      })
      .catch((err) => {
        console.error("updateCredentialStore failed:", err?.response?.status, err?.message);
      });
  };
  const updateCredentialShareMode = (e, val) => {
    let CredentialSharePayload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
      credentialsharemode: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setCredentialShareMode`, CredentialSharePayload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `Share Credentials updated successful to ${val.children}`,
        });
      })
      .catch((err) => {
        console.error("updateCredentialShareMode failed:", err?.response?.status, err?.message);
      });
  };

  const updateDITStatus = async (e) => {
    setDitStatus(e);
    let payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      status: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setDITStatus`, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        notification.open({
          type: "success",
          content: `DIT Status updated successful to ${e}`,
        });
      })
      .catch((err) => {
        console.error("updateDITStatus failed:", err?.response?.status, err?.message);
      });
  };

  const updateTenantTimezone = async (e) => {
    setTenantTimezone(e);
    let payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      timezone: e,
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/setTenantTimeZone`, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        localStorage.setItem("timezone", e);
        notification.open({
          type: "success",
          content: `Timezone updated successful to ${e}`,
        });
      })
      .catch((err) => {
        console.error("updateTenantTimezone failed:", err?.response?.status, err?.message);
      });
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf("/") + 1));
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSaveSession = async () => {
    if (isEmpty(sessionRecording?.sessionRecordingBucketName) || isEmpty(sessionRecording?.sessionRecordingKey) || isEmpty(sessionRecording?.sessionRecordingSecret) || isEmpty(sessionRecording?.sessionRecordingRegion)) {
      notification.error({ content: "Please fill all Session Recording Configuration fields" });
      return;
    }

    let payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      sessionRecordingKey: sessionRecording?.sessionRecordingKey,
      sessionRecordingSecret: sessionRecording?.sessionRecordingSecret,
      sessionRecordingRegion: sessionRecording?.sessionRecordingRegion,
      sessionRecordingBucketName: sessionRecording?.sessionRecordingBucketName,
    };

    try {
      await axios.post(
        `${REACT_APP_API_TENANTS_URL}/setTenantS3`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );


      //  Make sure we're in view mode after save
      handleSessionSetState({ isEdit: true, isSave: true, isUserEditable: false });
      message.success("Session Recording configuration saved successfully!");
    } catch (error) {
      console.error("Error saving Session Recording:", error?.response?.status, error?.message);
      message.error("Failed to save Session Recording.");
    }
  };

  return (
    <>
      {setNotification}
      {CustomVaultModal(isModalOpen, () => setIsModalOpen(false), handleOk)}
      <div className="onboarding-container an-tenant-config" style={isOnboardRoute ? { padding: 24 } : undefined}>
        {isOnBoarding && startTour()}
        <style>{TENANT_CONFIG_CSS}</style>

        <SettingsPage
          title="Tenant Configuration"
          description="Tenant-wide security defaults: sign-in factors and policy, credentials and secrets, connectivity, and session recording."
        >
          <SettingsSection title="General">
            <SettingsRow
              icon={<Building2 size={17} />}
              title="Tenant name"
              description="Set when the tenant was created. Contact support to change it."
              value={tenantData?.tenantname || "—"}
              last={isLoading}
            />
            {!isLoading && (
              <>
                <ConfigRow
                  {...rowProps("0")}
                  icon={<ImageIcon size={17} />}
                  title="Tenant logo"
                  description="The logo shown on sign-in and in the console."
                >
                    <TenantLogo notification={notification} tenantLogo={tenantLogo} setTenantLogo={setTenantLogo}/>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("12")}
                  icon={<Clock size={17} />}
                  title="Timezone"
                  description="The timezone used for schedules and timestamps across the tenant." last
                >
                    <Row gutter={16}>
                      <Col span={6}>
                        <p className="search-label">Select Timezone</p>

                        <Select
                          showSearch
                          value={tenantTimezone}
                          placeholder="Select Time Zone"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                          onChange={updateTenantTimezone}
                        >
                          {TIME_ZONES.map((tz) => (
                            <Option key={tz.value} value={tz.value}>
                              {tz.label}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                    </Row>
                </ConfigRow>
              </>
            )}
          </SettingsSection>

          {isLoading ? (
            <SettingsCard>
              <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
              </div>
            </SettingsCard>
          ) : (
            <>
              <SettingsSection title="Authentication" description="How people prove who they are when they sign in or reach a resource.">
                {/* The signed-in user's OWN second factors, not a tenant-wide
                    setting — hence the qualifier in the title, one row above
                    "Authentication factors", which is the tenant-wide one. */}
                {!isOnboardRoute && (
                <ConfigRow
                  {...rowProps("userMfa")}
                  icon={<User size={17} />}
                  title="Multi-factor authentication (your account)"
                  description="The second factors enrolled on your own account."
                >
<MfaFactorsPage embedded />
                </ConfigRow>
                )}
                <ConfigRow
                  {...rowProps("1")}
                  icon={<KeyRound size={17} />}
                  title="Authentication factors"
                  description="Which factors the tenant accepts and requires."
                >
                    <AuthFactors />
                    <Row gutter={16}>
                      {/* <Col span={24}> */}
                      {/* <Row gutter={16}>
                          <Col span={6} id="onboarding_step4">
                            <p className="search-label">Select SSO MFA</p>

                            <Select
                              defaultValue={tenantData.ssomfa}
                              onChange={(e, val) => {
                                updateSsoMfa(e, val);
                              }}
                              placeholder="Select SSO MFA"
                              disabled
                            >
                              <Option value={3}>Enabled</Option>
                              <Option value={0}>Disabled</Option>
                            </Select>
                          </Col>
                          <Col span={8}>
                            <p className="search-label" style={{ marginLeft: "9px" }}>
                              Use SSO MFA Caching for Endpoint Authentication{" "}
                            </p>
                            <Col span={20}>
                              <Select
                                defaultValue={tenantData.adminmfacache}
                                onChange={updateAdminMFACache}
                              >
                                <Option value={3}>Enabled</Option>
                                <Option value={0}>Disabled</Option>
                              </Select>
                            </Col>
                          </Col>
                        </Row> */}
                      {/* <Row gutter={16}>      
                      <Col span={6}>
                        <p className="search-label">
                          {" "}
                          Select SSO MFA(Enduser Console)
                        </p>

                        <Select
                          defaultValue={tenantData.ssomfaenduser}
                          onChange={(e, val) => {
                            updateSsoMfaUser(e, val);
                          }}
                        >
                          <Option value={3}>Enable</Option>
                          <Option value={0}>Disable</Option>
                        </Select>
                      </Col>
                      <>
                        <Col span={8}>
                          <p
                            className="search-label"
                            style={{ marginLeft: "9px" }}
                          >
                            Show Push Notification for Endpoint Authentication
                          </p>

                          <Col span={20}>
                            <Select
                              defaultValue={tenantData.endusermfacache}
                              onChange={updateEnduserMFACache}
                            >
                              NOTE : yes show push notification(mfa cache True)
                              <Option value={0}>Yes</Option>
                              NOTE: dont show push notification(mfa cache False)
                              <Option value={3}>No</Option>
                            </Select>
                          </Col>
                        </Col>
                      </>
                    </Row> */}

                      {/* <Col span={6}> Below fields will needed in future
                        <p className="search-label">Service Account MFA</p>

                        <Select defaultValue={1}>
                          <Option value={0}>No MFA - use service account policie</Option>
                          <Option value={1}>
                            Use MFA for services (defaults to application MFA)
                          </Option>
                        </Select>
                      </Col> */}
                      {/* Below fields will needed in future */}

                      {/* <Row gutter={16}>
                        <Col span={6}>
                        <p className="search-label">Application MFA (multi-select)</p>

                        <Select
                          defaultValue={
                            tenantData?.platformmfa === 0 ? "" : tenantData?.platformmfa
                          }
                          onChange={(e) => {
                            updatePlatformMfa(e);
                          }}
                        >
                          <Option value={1}>No MFA (use sso caching) </Option>
                          <Option value={2}>Decentralized credentials </Option>
                          <Option value={3}>Biometric MFA on all auth</Option>
                          <Option value={4}>App based MFA</Option>
                        </Select>
                      </Col>
                        <Col span={6}>
                        <span id="onboarding_step2">
                          <p className="search-label">
                            AD / Kerberos / Endpoint Authentication MFA
                          </p>

                          <Select
                            defaultValue={
                              tenantData?.entityauthentication === 0
                                ? ""
                                : tenantData?.entityauthentication
                            }
                            onChange={updateEntityAuthentication}
                          >
                            <Option value={1}>No MFA (use sso caching) </Option>
                            <Option value={2}>Credential + Biometric MFA on all auth </Option>
                            <Option value={3}>Credential + OTP on all auth</Option>
                          </Select>
                        </span>
                      </Col>
                        </Row> */}
                      {/* </Col> */}
                    </Row>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("4")}
                  icon={<FileText size={17} />}
                  title="Authentication policy"
                  description="Audit, simulate, or enforce authentication policies."
                >
                    <Row gutter={16}>
                      <Col span={12} id="onboarding_step3">
                        <p className="search-label">Authentication Policy</p>

                        <Select
                          defaultValue={tenantData?.authenticationpolicy}
                          onChange={updateAuthenticationPolicy}
                        >
                          <Option value={1}>
                            Audit - this will audit existing policies and create new policies (learn
                            the system)
                          </Option>
                          <Option value={2}>
                            Policy - Simulate enforcement policies and see the impact of blocked
                            policies
                          </Option>
                          <Option value={3}>
                            Live mode - Zero trust (block everything but trusted) - warning this
                            requires policies to be available
                          </Option>
                        </Select>
                      </Col>
                    </Row>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("13")}
                  icon={<LogIn size={17} />}
                  title="SSO configuration"
                  description="Single sign-on methods for this tenant."
                >
                    <SsoConfig message={message}/>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("15")}
                  icon={<ShieldAlert size={17} />}
                  title="Conditional access"
                  description="Allow or deny sign-in based on conditions."
                >
                    <ConditionalAccess tenantData={tenantData} message={message}/>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("9")}
                  icon={<Network size={17} />}
                  title="Direct AD authentication"
                  description="Allow AD domain authentication without policies." last
                >
                    {" "}
                    <Row gutter={16}>
                      <Col span={6}>
                        <p className="search-label">Enable Ad domain authentication</p>

                        <Select defaultValue={0}>
                          <Option value={1}>Yes</Option>
                          <Option value={0}> No</Option>
                        </Select>
                      </Col>
                      <Col span={6}>
                        <p className="search-label">Enable dynamic authz</p>

                        <Select defaultValue={0}>
                          <Option value={1}>Yes</Option>
                          <Option value={0}>No</Option>
                        </Select>
                      </Col>
                    </Row>
                </ConfigRow>
              </SettingsSection>

              <SettingsSection title="Access & credentials" description="Where credentials and secrets live, how they are shared, and emergency access.">
                <ConfigRow
                  {...rowProps("3")}
                  icon={<Lock size={17} />}
                  title="Credential store and sharing"
                  description="Where credentials are stored and how they are shared."
                >
                    <Row gutter={16}>
                      <Col span={6}>
                        <p className="search-label">Credential Store</p>

                        <Select
                          defaultValue={tenantData?.credentialstore}
                          onChange={updateCredentialStore}
                        >
                          <Option value={1}>Decentralize</Option>
                          {/* Options will needed in future */}
                          {/* <Option value={2}>Use a vault</Option> */}
                        </Select>
                      </Col>
                      <Col span={6}>
                        <p className="search-label">Share Credentials</p>

                        <Select
                          defaultValue={tenantData?.credentialsharemode}
                          onChange={updateCredentialShareMode}
                        // mode="multiple"
                        >
                          <Option value={1}>Share to email </Option>
                          {/* Retired wallet sharing mode: shown only so a tenant that still has it stored doesn't render a bare "2". */}
                          {tenantData?.credentialsharemode === 2 && (
                            <Option value={2} disabled>
                              Retired sharing mode
                            </Option>
                          )}
                          <Option value={3}>Share via SSO only</Option>
                        </Select>
                      </Col>
                      <Col span={6}>
                        <p className="search-label">Credential Mode</p>

                        <Select
                          onChange={updateAuthenticationMechanism}
                          defaultValue={tenantData?.credentialstore}
                        >
                          <Option value={0}>Default</Option>
                          <Option value={1}>Policy</Option>
                        </Select>
                      </Col>

                      {/* <Col span={6}>
                    <p className="search-label">Service Account MFA</p>

                    <Select>
                      <Option value={1}>No MFA - use service account policie</Option>
                      <Option value={2}>Use MFA for services (defaults to application MFA)</Option>
                    </Select>
                  </Col> */}
                    </Row>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("6")}
                  icon={<BadgeCheck size={17} />}
                  title="Issuer"
                  description="The default issuer for this tenant."
                >
                    <div className="space-y-4">
                      <Row gutter={16}>
                        <Col span={6}>
                          <p className="search-label">Select Issuer to make it as default issuer</p>
                          <Select
                            showSearch
                            value={defaultIssuerId}
                            onChange={(value) => setDefaultIssuerId(value)} 
                            options={issuerDetails}
                          />
                        </Col>
                      </Row>
                      <div className="px-5 flex justify-start w-full">
                          <Button className="px-7" onClick={()=> handleConfigureDefaultIssuer()} type="primary">Update</Button>
                      </div>
                    </div>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("10")}
                  icon={<Archive size={17} />}
                  title="Secret management"
                  description="Keep secrets in AuthNull or in your own vault."
                >
                    <Row gutter={16}>
                      <Col span={12}>
                        <p className="search-label">Vault Type</p>

                        <Select
                          placeholder="Select vault type"
                          onChange={updateVaultType}
                          value={vaultDetails.vaultType}
                        >
                          <Option value="Default">
                            Default (No external vault)
                          </Option>
                          <Option value="Centralized Vault">
                            Centralized Vault (Secrets will be maintained in Authnull Vault)
                          </Option>
                          <Option value="Custom Vault">
                            Custom Vault (Secrets will be maintained in Customer's Vault)
                          </Option>
                        </Select>
                      </Col>
                      {vaultDetails.vaultType === "Custom Vault" &&
                        vaultDetails.rootToken.length > 0 && (
                          <Col span={12}>
                            <div
                              style={{
                                padding: "10px",
                                paddingLeft: "20px",
                                backgroundColor: "#efefef",
                                borderRadius: "10px",
                              }}
                            >
                              <p className="search-label">Vault Details</p>
                              <p style={{ marginBottom: 0, marginTop: 0 }}>
                                Vault Type: {vaultDetails.vaultType}
                              </p>
                              <p style={{ marginBottom: 0, marginTop: 0 }}>
                                Root token: {vaultDetails.rootToken}
                              </p>
                              <p style={{ marginBottom: 0, marginTop: 0 }}>
                                IP Address: {vaultDetails.ipAddress}
                              </p>
                              <p style={{ marginTop: 0 }}>Port: {vaultDetails.port}</p>
                            </div>
                          </Col>
                        )}
                    </Row>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("5")}
                  icon={<AlertTriangle size={17} />}
                  title="Break glass"
                  description="Emergency root access to the tenant."
                >
                    <div className="px-6 py-2 font-medium text-base">
                      <div className="space-x-3">
                        <Text className="m-0">Disable root access</Text>
                        <Switch size="small" checked={breakglassDisable} onChange={handleBreakglass} />
                      </div>
                    </div>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("11")}
                  icon={<Tags size={17} />}
                  title="Dynamic Identity Tapestry"
                  description="Dynamic authorization policy for identities."
                >
                    <Row gutter={16}>
                      <Col span={6}>
                        <p className="search-label">Authorization Policy</p>

                        <Select
                          placeholder="Select Authorization Policy"
                          onChange={updateDITStatus}
                          value={ditStatus}
                          options={[
                            { label: "Enabled", value: "Enabled" },
                            { label: "Disabled", value: "Disabled" },
                          ]}
                        ></Select>
                      </Col>
                    </Row>
                </ConfigRow>
                <ConfigRow
                  {...rowProps("2")}
                  icon={<AppWindow size={17} />}
                  title="Application control"
                  description="How application activity is audited or enforced." last
                >
                    {" "}
                    <Row gutter={16}>
                      <Col span={12}>
                        <p className="search-label">Action</p>

                        <Select defaultValue={1}>
                          <Option value={1}>
                            Audit everything (audit and generate policies automatically)
                          </Option>
                          <Option value={2}>
                            Live mode (enforcement mode) also a zero trust mode when uac is on. New
                            policies are also created in blocked status{" "}
                          </Option>
                          <Option value={3}>
                            Policy mode (simulate enforcement and generate new policies). New
                            policies are created in blocked status
                          </Option>
                        </Select>
                      </Col>
                    </Row>
                </ConfigRow>
                  {/* Below fields will needed in future */}

                  {/* <Panel header="Service Account" key="5">
                <Row gutter={16}>
                  <Col span={6}>
                    <p className="search-label">Discovery of service accounts</p>

                    <Select>
                      <Option value={0}>Discover and manage with policies</Option>
                      <Option value={1}> Ignore service accounts</Option>
                    </Select>
                  </Col>
                  <Col span={6}>
                    <p className="search-label">Authentication</p>

                    <Select>
                      <Option value={1}>
                        Zero trust (block everything but trusted) - warning this requires policies
                        to be available{" "}
                      </Option>
                      <Option value={2}>
                        {" "}
                        Audit Mode - this will audit existing policies and create new policies
                      </Option>
                      <Option value={3}>
                        Policy - Simulate enforcement policies and see the impact of blocked
                        policies
                      </Option>
                    </Select>
                  </Col>
                </Row>
              </Panel> */}
                  {/* Below fields will needed in future */}

                  {/* <Panel header="Revoke local admin credentials" key="6">
                {" "}
                <Row gutter={16}>
                  <Col span={6}>
                    <p className="search-label">Windows</p>

                    <Select>
                      <Option value={0}>Revoke admin credentials</Option>
                      <Option value={1}> Ignore</Option>
                    </Select>
                  </Col>
                  <Col span={6}>
                    <p className="search-label">Linux</p>

                    <Select>
                      <Option value={0}>Revoke admin credentials</Option>
                      <Option value={1}>Ignore</Option>
                    </Select>
                  </Col>
                </Row>
              </Panel> */}
              </SettingsSection>

              <SettingsSection title="Connectivity & recording" description="How endpoints connect, and where session recordings are stored.">
                <ConfigRow
                  {...rowProps("7")}
                  icon={<Plug size={17} />}
                  title="Connection"
                  description="Connect through the AuthNull proxy, directly, or both."
                >
                    {" "}
                    <Row gutter={16}>
                      <Col span={6}>
                        <p className="search-label">Connection Mode</p>

                        <Select
                          defaultValue={tenantData?.connectionmode}
                          onChange={updateConnectionMode}
                        >
                          <Option value={1}>Authnull Proxy</Option>
                          <Option value={2}> Direct</Option>
                          <Option value={3}> Direct and Authnull Proxy</Option>
                        </Select>
                      </Col>
                    </Row>
                </ConfigRow>
                  {/* Below fields will needed in future */}

                  {/* <Panel header="Risk based mfa and dynamic authz" key="8">
                {" "}
                <Row gutter={16}>
                  <Col span={6}>
                    <p className="search-label">Enable risk based mfa</p>

                    <Select>
                      <Option value={0}>Yes</Option>
                      <Option value={1}> No</Option>
                    </Select>
                  </Col>
                  <Col span={6}>
                    <p className="search-label">Enable dynamic authz</p>

                    <Select>
                      <Option value={1}>Yes</Option>
                      <Option value={2}>No</Option>
                    </Select>
                  </Col>
                </Row>
              </Panel> */}
                  {/* <Panel header="Authentication Mechanism" key="8">
                {" "}
                <Row gutter={16}>
                  <Col span={6}>
                    <p className="search-label">Authentication Mode</p>

                    <Select onChange={updateAuthenticationMechanism}>
                      <Option value={0}>Default</Option>
                      <Option value={1}>Policy</Option>
                    </Select>
                  </Col>
                </Row>
              </Panel> */}
                <ConfigRow
                  {...rowProps("14")}
                  icon={<Video size={17} />}
                  title="Session recording"
                  description="The S3 bucket session recordings are written to." last
                >
                    <div className="space-y-4 ms-6">
                      <Row gutter={20} className="flex items-center">
                        <Col span={6}>
                          <p className="search-label"><span className="text-red-600 text-base">*</span> S3 Bucket name</p>
                          <UserInput
                            inputId={"S3 Bucket name"}
                            required={true}
                            placeHolder={"Please enter a S3 Bucket name"}
                            // disabled={sessionRecording?.isEdit && !sessionRecording?.isUserEditable}
                            value={sessionRecording?.sessionRecordingBucketName}
                            onChange={(e) => handleSessionSetState({ sessionRecordingBucketName: e.target.value })}
                          />
                        </Col>
                        <Col span={6}>
                          <p className="search-label"><span className="text-red-600 text-base">*</span> S3 Key</p>
                          <UserInput
                            inputId={"s3key"}
                            required={true}
                            placeHolder={"Please enter a S3 key"}
                            // disabled={sessionRecording?.isEdit && !sessionRecording?.isUserEditable}
                            value={sessionRecording?.sessionRecordingKey}
                            onChange={(e) => handleSessionSetState({ sessionRecordingKey: e.target.value })}
                          />
                        </Col>
                        <Col span={6}>
                          <p className="search-label"><span className="text-red-600 text-base">*</span> S3 Secret</p>
                          <UserInput
                            inputId={"s3secret"}
                            required={true}
                            placeHolder={"Please enter a S3 Secret"}
                            // disabled={sessionRecording?.isEdit && !sessionRecording?.isUserEditable}
                            value={sessionRecordingSecret}
                            onChange={handleMaskedInput}
                            onPaste={handlePaste}
                          />
                        </Col>
                        <Col span={6}>
                          <p className="search-label"><span className="text-red-600 text-base">*</span> S3 Region</p>
                          <UserSelect
                            inputId={"s3Region"}
                            showSearch={true}
                            placeHolder={"Select the S3 Region"}
                            required={true}
                            options={awsServerRegion}
                            // disabled={sessionRecording?.isEdit && !sessionRecording?.isUserEditable}
                            value={sessionRecording?.sessionRecordingRegion}
                            onChange={(value) => handleSessionSetState({ sessionRecordingRegion: value })}
                          />
                        </Col>
                        {/* <Col
                          span={2}
                          className="flex flex-col items-center justify-center"
                        >
                          {sessionRecording?.isEdit && !sessionRecording?.isUserEditable && (
                            <Button
                              size="small"
                              type="primary"
                              className="w-full mt-8"
                              onClick={() => {
                                handleSessionSetState({ isEdit: true, isUserEditable: true, isSave: false })
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        </Col> */}
                      </Row>
                      <Row>
                        <Col>
                          <Button
                            type="primary"
                            onClick={handleSaveSession}
                          // disabled={sessionRecording?.isSave}
                          >
                            Save Configuration
                          </Button>
                        </Col>
                      </Row>
                    </div>
                </ConfigRow>
              </SettingsSection>
            </>
          )}
        </SettingsPage>

        <DataInsertModal
          className="data-insert-modal"
          title={"Authentication Provider"}
          isOpen={isOpenAuthProvider}
          handleDataValue={handleAddMFAProvider}
          handleModelClose={() => setOpenAuthProvider(false)}
          children={
            <Form
              onFinish={() => { }}
              form={newMFAForm}
              layout="vertical"
            >
              <FormInput
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Please enter the Name.' }]}
              />
              <FormInput
                label="Description"
                name="description"
                rules={[{ required: true, message: 'Please enter the Description.' }]}
              />
            </Form>
          }
        />
      </div>
    </>
  );
};

export default TenantConfig;
